import {
  ExpandedState,
  RowData,
  SortingState,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import useColumns from './useColumns';
import { TableProps } from '../types';

type useTableProps<TData> = Pick<
  TableProps<TData>,
  | 'isInitialLoading'
  | 'isFetchingNext'
  | 'data'
  | 'columns'
  | 'options'
  | 'getRowId'
  | 'renderRowExpand'
>;

function useTable<TData extends RowData>({
  isInitialLoading,
  isFetchingNext,
  data,
  columns,
  renderRowExpand,
  options: { placeholderCount, isColumnResizeable, isDataSortable, isRowExpandable },
  getRowId,
}: useTableProps<TData>) {
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const [expandedState, setExpandedState] = useState<ExpandedState>({});

  const internalColumns = useColumns<TData>(columns, renderRowExpand);

  const table = useReactTable<TData>({
    data,
    columns: internalColumns,
    columnResizeMode: isColumnResizeable ? 'onChange' : undefined,
    state: {
      sorting: isDataSortable ? sortingState : undefined,
      expanded: isRowExpandable ? expandedState : undefined,
    },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    getRowCanExpand: () => isRowExpandable || false,
    onSortingChange: isDataSortable ? setSortingState : undefined,
    onExpandedChange: isRowExpandable ? setExpandedState : undefined,
  });

  return table;
}

export default useTable;
